import React, { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowRight, MapPin } from "lucide-react";

/**
 * Site-wide "Meet the team" modal + floating pill.
 *
 * Architecture:
 *  - <MeetTheTeamProvider> wraps the app and owns the open/close state.
 *  - <MeetTheTeamFloatingPill /> renders a persistent bottom-left pill on every page.
 *  - <MeetTheTeamLink /> is a small inline link any CTA can drop in.
 *  - useMeetTheTeam() exposes { open } so anything can pop the modal.
 */

const Ctx = createContext({ open: () => {} });
export const useMeetTheTeam = () => useContext(Ctx);

const TeamModalBody = ({ onClose }) => (
  <div className="text-[var(--af-cream)]" data-testid="meet-team-modal">
    {/* Hero composite */}
    <div className="relative bg-gradient-to-b from-[#0a3a5c] to-[var(--af-navy)] -mx-6 -mt-6 px-6 pt-8 pb-2 border-b border-[var(--af-line)]">
      <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)] flex items-center gap-2 mb-3">
        <MapPin size={12} /> Charlottesville, Virginia
      </div>
      <img
        src="/media/team/anne-collean.webp"
        alt="Anne Flinn and Collean Laney"
        className="w-full h-auto block select-none pointer-events-none"
        draggable="false"
        data-testid="meet-team-composite"
      />
    </div>

    {/* Names + roles */}
    <div className="mt-5 grid grid-cols-2 gap-4 text-center sm:text-left">
      <div data-testid="meet-team-collean">
        <div className="font-display text-xl sm:text-2xl leading-tight">
          Collean Laney
        </div>
        <div className="text-xs uppercase tracking-widest text-[var(--af-lime)] mt-1">
          Director of Accounts
        </div>
      </div>
      <div data-testid="meet-team-anne">
        <div className="font-display text-xl sm:text-2xl leading-tight">
          Anne Flinn
        </div>
        <div className="text-xs uppercase tracking-widest text-[var(--af-lime)] mt-1">
          Founder &amp; Principal Strategist
        </div>
      </div>
    </div>

    {/* Trust copy */}
    <p className="mt-6 text-sm sm:text-base text-[var(--af-cream)]/85 leading-relaxed">
      Anne and Collean are the team behind this website. When you ask for a
      meeting, you talk directly to one of us, not a sales rep. We put this
      site together based on decades of industry experience across tourism and
      home services, and we want you to trust us the same way you&apos;d want your
      own customer to trust your best recommendation in your industry.
    </p>

    {/* CTA */}
    <div className="mt-7 flex flex-col sm:flex-row gap-3">
      <a
        href="/#lead-form"
        onClick={onClose}
        className="btn-lime justify-center flex-1"
        data-testid="meet-team-cta-call"
      >
        Set up a 20-min call <ArrowRight size={14} />
      </a>
      <button
        type="button"
        onClick={onClose}
        className="btn-ghost-lime justify-center flex-1"
        data-testid="meet-team-cta-close"
      >
        Keep browsing
      </button>
    </div>
  </div>
);

export const MeetTheTeamProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const value = { open: useCallback(() => setOpen(true), []) };
  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-xl w-[calc(100%-2rem)] sm:w-full bg-[var(--af-navy-deep,#001f33)] border border-[var(--af-line)] text-[var(--af-cream)] sm:rounded-2xl overflow-hidden p-6"
          data-testid="meet-team-dialog-content"
        >
          <TeamModalBody onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
};

/**
 * Persistent floating pill in the bottom-left, always visible.
 * Opens the team modal on click.
 */
export const MeetTheTeamFloatingPill = () => {
  const { open } = useMeetTheTeam();
  return (
    <button
      type="button"
      onClick={open}
      className="fixed left-3 bottom-3 sm:left-5 sm:bottom-5 z-30 group flex items-center gap-2 sm:gap-3 pl-1 pr-3 sm:pr-4 py-1 sm:py-1.5 bg-[#001f33]/95 backdrop-blur border border-[var(--af-lime)]/40 hover:border-[var(--af-lime)] rounded-full shadow-[0_8px_24px_-6px_rgba(0,0,0,0.5)] transition-all hover:-translate-y-0.5"
      aria-label="Meet Anne and Collean, the team behind this site"
      data-testid="meet-team-floating-pill"
    >
      <img
        src="/media/team/anne-collean-pill.webp"
        alt=""
        className="h-9 sm:h-11 w-auto pointer-events-none select-none"
        draggable="false"
      />
      <span className="text-[10px] sm:text-xs uppercase tracking-widest text-[var(--af-cream)] group-hover:text-[var(--af-lime)] font-medium whitespace-nowrap">
        Meet Anne &amp; Collean
      </span>
    </button>
  );
};

/**
 * Quiet inline link to drop under primary CTAs.
 */
export const MeetTheTeamLink = ({ className = "" }) => {
  const { open } = useMeetTheTeam();
  return (
    <button
      type="button"
      onClick={open}
      className={
        "inline-flex items-center gap-1 text-[11px] sm:text-xs text-[var(--af-cream)]/65 hover:text-[var(--af-lime)] underline-offset-4 hover:underline transition-colors " +
        className
      }
      data-testid="meet-team-inline-link"
    >
      Meet the team behind this <ArrowRight size={11} />
    </button>
  );
};
